import React, { useEffect, useState } from 'react';
import { differenceInDays } from 'date-fns';
import { fetchAllArticles } from '../services/articleService';
import { Article } from '../types/article';
import ArticleCard from '../components/articles/ArticleCard';
import LoadingIndicator from '../components/common/LoadingIndicator';
import EmptyState from '../components/common/EmptyState';

const TopStoriesPage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    document.title = 'Top Stories | Legal Nest';
    
    const loadTopStories = async () => {
      try {
        setLoading(true);
        
        // Fetch all articles
        const allArticles = await fetchAllArticles();
        
        // Filter articles from the last 2 days
        const now = new Date();
        const recentArticles = allArticles.filter(article => {
          const articleDate = new Date(article.date);
          return differenceInDays(now, articleDate) <= 2; // Articles from last 2 days
        });
        
        // Sort by date (newest first)
        recentArticles.sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        
        setArticles(recentArticles);
        setLoading(false);
      } catch (err) {
        console.error('Error loading top stories:', err);
        setError('Failed to load top stories');
        setLoading(false);
      }
    };
    
    loadTopStories();
  }, []);
  
  if (loading) {
    return (
      <div className="container-custom py-8">
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6">Top Stories</h1>
        <LoadingIndicator message="Loading top stories..." />
      </div>
    );
  }
  
  return (
    <div className="container-custom py-8">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-3">Top Stories</h1>
        <p className="text-neutral-600">
          Latest legal news and updates from the past 48 hours.
        </p>
      </div>
      
      {error && (
        <div className="bg-red-50 p-4 rounded-lg text-red-600 mb-8">
          {error}
        </div>
      )}
      
      {!error && articles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      ) : (
        <EmptyState 
          title="No Top Stories Available"
          message="We don't have any stories from the past 48 hours. Check back soon for updates!"
          type="writing"
        />
      )}
    </div>
  );
};

export default TopStoriesPage;